'use client'

/**
 * Marks for the things Connect reaches: the data sources on one side, the AI
 * apps on the other.
 *
 * Sources resolve through `integrationIcon()` — the product's own registry —
 * rather than a second platform → icon map. A hand-maintained copy is exactly
 * the failure that file's own comment warns about: it keeps rendering the old
 * mark after a card swaps its icon, and nothing goes red. What lives here is
 * only the part that genuinely differs: an ALIAS table, because Connect's
 * platform ids and the Integrations card ids are two namespaces that mostly —
 * but not always — coincide.
 *
 * A platform with no card still renders: a monogram tile, same footprint, same
 * baseline. A hole in a grid reads as a broken image, and Connect's platform
 * table is appended to at runtime by MCP vendors, so "no card for this id" is a
 * state that will keep occurring rather than a bug to be fixed once.
 *
 * Client component on purpose. The registry builds Iconify `<Icon>` elements,
 * which are client-side; the landing page is a server component and renders
 * these as client children.
 */

import { Icon } from '@iconify/react'
import {
  AudioLines,
  BarChart3,
  Bot,
  Globe,
  Megaphone,
  MessagesSquare,
  Search,
  Smartphone,
  Sparkles,
  TrendingUp,
  Users,
  Video,
} from 'lucide-react'

import { integrationIcon } from '@/lib/integrations'
import { resolveHostMark } from '@/lib/connect/host-marks'
import type { ConnectSokuSupply } from '@/lib/connect/supply'
import { cn } from '@/lib/utils'

/**
 * Connect platform id → Integrations card id, where the two differ.
 *
 * Only genuine mismatches belong here. `chatgpt_ads` is the platform key in
 * `apps/api/routers/agent_api/data/_platform_registry.py`; the card that owns
 * that logo is `chatgpt`. Everything else (`ga4`, `gsc`, `meta`, `google`,
 * `tiktok`, `linkedin`, and the MCP vendors) already matches, and listing those
 * here would turn an index into the second map this module exists to avoid.
 */
const CARD_ID_BY_PLATFORM: Record<string, string> = {
  chatgpt_ads: 'chatgpt',
}

const BRAND_MARK_BY_PLATFORM: Record<
  string,
  { viewBox: string; path: string; tint: string }
> = {
  ahrefs: {
    viewBox: '0 0 16 16',
    path: 'M3 1v3h8v2l-3.263.057C3.35 6.464 2 7.51 2 10.62v.814C2 13.817 3.791 15 6 15c.998 0 2.04-.195 3.13-.747L11 13.5V15h3V1H3Zm8 9.5-2.264.934c-.787.436-1.604.668-2.24.668C5.71 12.102 5 11.96 5 11v-.64c.06-1.017.922-1.397 3.282-1.629L11 8.5v2Z',
    tint: 'text-[#ff8800]',
  },
  similarweb: {
    viewBox: '0 0 24 24',
    path: 'M22.099 5.781c-1.283-2-3.14-3.67-5.27-4.52l-.63-.213a7.433 7.433 0 0 0-2.15-.331c-2.307.01-4.175 1.92-4.175 4.275a4.3 4.3 0 0 0 .867 2.602l-.26-.342c.124.186.26.37.417.556.663.802 1.604 1.635 2.822 2.58 2.999 2.32 4.943 4.378 5.104 6.93.038.344.062.696.062 1.051 0 1.297-.283 2.67-.764 3.635h.005s-.207.377-.077.487c.066.057.21.1.46-.053a12.104 12.104 0 0 0 3.4-3.33 12.111 12.111 0 0 0 2.088-6.635 12.098 12.098 0 0 0-1.9-6.692zm-9.096 8.718-1.878-1.55c-3.934-2.87-5.98-5.966-4.859-9.783a8.73 8.73 0 0 1 .37-1.016v-.004s.278-.583-.327-.295a12.067 12.067 0 0 0-6.292 9.975 12.11 12.11 0 0 0 2.053 7.421 9.394 9.394 0 0 0 2.154 2.168H4.22c4.148 3.053 7.706 1.446 7.706 1.446h.003a4.847 4.847 0 0 0 2.962-4.492 4.855 4.855 0 0 0-1.889-3.87z',
    tint: 'text-[#0b2945]',
  },
}

/**
 * The sizes `integrationIcon` accepts. `xs` exists on the icon components but
 * not on that lookup, so it is deliberately absent here rather than widened —
 * a size the resolver cannot serve would be a compile error at every call site
 * except the one that matters.
 */
type IconSize = 'sm' | 'md' | 'lg'

/**
 * Built once per size and reused.
 *
 * `integrationIcon` constructs the entire card registry on every call — fine
 * for the one-off lookup it was written for, wasteful for a grid of twenty.
 * This is an INDEX of that registry, not a copy of it: it holds whatever the
 * registry returns today, so a card swapping its icon still propagates.
 */
const INDEX_BY_SIZE = new Map<IconSize, Map<string, React.ReactNode>>()

function iconFor(platform: string, size: IconSize): React.ReactNode {
  let index = INDEX_BY_SIZE.get(size)
  if (!index) {
    index = new Map()
    INDEX_BY_SIZE.set(size, index)
  }
  const cardId = CARD_ID_BY_PLATFORM[platform] ?? platform
  if (!index.has(cardId)) index.set(cardId, integrationIcon(cardId, size))
  return index.get(cardId) ?? null
}

/** Last resort: the source's own initial, on the same footprint as a logo. */
function Monogram({ label }: { label: string }) {
  return (
    <span
      aria-hidden
      className="bg-muted text-muted-foreground flex size-7 items-center justify-center rounded-md text-xs font-semibold"
    >
      {label.trim().charAt(0).toUpperCase() || '?'}
    </span>
  )
}

/**
 * The mark for one data source.
 *
 * `displayName` is used only by the fallback, so a source with no card still
 * reads as itself rather than as a question mark.
 *
 * `iconUrl` is for a Soku-supplied vendor, which has no Integrations card: pass
 * the logo `vendorFromId()` already resolves for the chat trace, rather than
 * adding a third vendor → logo table. It is consulted after the inline brand
 * marks and the card registry, and before the monogram.
 */
export function ConnectSourceIcon({
  platform,
  displayName = '',
  size = 'sm',
  iconUrl,
  className,
}: {
  platform: string
  displayName?: string
  size?: IconSize
  iconUrl?: string | null
  className?: string
}) {
  const icon = iconFor(platform, size)
  const brandMark = BRAND_MARK_BY_PLATFORM[platform]
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center',
        size === 'sm' && 'size-7',
        size === 'md' && 'size-12',
        className,
      )}
    >
      {brandMark ? (
        <svg
          viewBox={brandMark.viewBox}
          className={cn('size-full', brandMark.tint)}
          aria-hidden
        >
          <path fill="currentColor" d={brandMark.path} />
        </svg>
      ) : (
        (icon ??
        (iconUrl ? (
          // A plain <img>: these are small, fixed-size vendor logos from two
          // known origins, and `next/image` would need each origin allowlisted
          // for no resizing benefit at this size.
          <img src={iconUrl} alt="" className="size-full object-contain" />
        ) : (
          <Monogram label={displayName || platform} />
        )))
      )}
    </span>
  )
}

/**
 * A glyph per Soku-supplied category — Lucide rather than vendor logos.
 *
 * A category is served by one or more vendors that can change without the
 * category changing. Printing one vendor's mark would name a supplier the
 * product does not promise to keep, so the category gets a glyph and the
 * vendor stays in the data.
 *
 * Typed as a total map over the union, so adding a category to
 * `CONNECT_SOKU_SUPPLY` without a glyph is a compile error rather than a blank
 * square on the landing page.
 */
const SUPPLY_ICON: Record<
  ConnectSokuSupply['icon'],
  React.ComponentType<{ className?: string }>
> = {
  search: Search,
  megaphone: Megaphone,
  globe: Globe,
  trending: TrendingUp,
  chart: BarChart3,
  smartphone: Smartphone,
  users: Users,
  video: Video,
  audio: AudioLines,
  sparkles: Sparkles,
  social: MessagesSquare,
}

/**
 * The mark for one Soku-supplied category.
 *
 * Rendered on the landing page's supply grid and in the demo's call rows and
 * ledger, which is why it lives here rather than in either one: two copies of
 * this map would drift the first time a category changed its glyph, and the
 * page whose job is to say what Connect reaches would be the one showing the
 * stale mark.
 */
export function ConnectSupplyIcon({
  icon,
  className,
}: {
  icon: ConnectSokuSupply['icon']
  className?: string
}) {
  const Glyph = SUPPLY_ICON[icon]
  return <Glyph className={cn('size-[18px]', className)} />
}

/**
 * The mark for one AI app.
 *
 * `onDark` is the ground this is being drawn on, not a style preference: a
 * colour lockup carries a fixed colour, and two of the ones here disappear or
 * dim against the near-black of a selected pill or the dusk painting behind
 * the hero. Which form each app has for each ground lives in
 * `host-marks.ts`, so the choice can be asserted without rendering.
 *
 * `custom` ("Your own agent") has no logo to use, and its fallback glyph is
 * `currentColor` already — one shape for both grounds.
 */
export function ConnectHostIcon({
  hostId,
  onDark = false,
  className,
}: {
  hostId: string
  onDark?: boolean
  className?: string
}) {
  const mark = resolveHostMark(hostId, { onDark })
  const classes = cn('size-6 shrink-0', className)
  if (!mark) return <Bot className={cn(classes, 'opacity-70')} aria-hidden />
  return (
    <Icon icon={mark.name} className={cn(classes, mark.tint)} aria-hidden />
  )
}
